// Trwałość: dwa magazyny IndexedDB, `utwory` (jedyne źródło prawdy dla
// wszystkiego, co gitarzysta widzi na liście) i `setlisty` (etap 8, kolejność
// grania). Ten moduł nie wie nic o kompilatorze, o zasiewie korpusu ani o
// eksporcie/imporcie całości - to role `zasiew.ts` i `plik.ts`.
//
// Eksport pliku zostaje realnym zabezpieczeniem śpiewnika (patrz `plik.ts`),
// ale NIE dlatego, że Safari czyści dane po dłuższej nieaktywności: domena
// aplikacji dodanej do ekranu głównego jest z tego mechanizmu wyłączona
// (sprawdzone 28.08.2026 na webkit.org/tracking-prevention/), jej dane są
// trzymane odrębnie od zwykłej karty Safari. Powodem jest coś innego:
// usunięcie całego originu pod presją miejsca na urządzeniu i ręczne
// czyszczenie danych przez użytkownika.

export type UtworId = string;

export interface Utwor {
  id: UtworId;
  /** Tytuł POKAZYWANY na liście. Wpisuje go człowiek, nie kompilator. */
  tytul: string;
  wykonawca: string | null;
  /** `wbudowany` = zasiany z korpusu w paczce, `reczny` = wklejony przez człowieka. */
  zrodlo: 'wbudowany' | 'reczny';
  rawTab: string;
  dodanyO: string; // ISO 8601
  zmienionyO: string | null;
  /** Zmiana wysokości brzmienia, w półtonach. Dodatnie = wyżej. Opcjonalne i
   * domyślnie 0: IndexedDB nie ma schematu na poziomie rekordu, więc rekordy
   * zapisane przed etapem 7 po prostu tego pola nie mają - uzupełnia się je
   * zerem przy odczycie (patrz `uzupelnijStroj` niżej), w jednym miejscu, nie
   * w każdym wywołującym. */
  transpozycja?: number;
  /** Kapodaster na progu N. Zmienia CHWYTY, nie brzmienie - patrz
   * `src/render/transpozycja.ts` po arytmetykę przesunięcia. Ten sam status
   * "opcjonalne, domyślnie 0" co `transpozycja`. */
  capo?: number;
  /** Etykiety nadane przez człowieka (gatunek, okazja, cokolwiek). OPCJONALNE
   * z tego samego powodu co `transpozycja`/`capo`: rekordy sprzed tego etapu
   * po prostu nie mają tego pola - uzupełnia się je pustą tablicą przy
   * odczycie (patrz `uzupelnijPola` niżej), w jednym miejscu. */
  tagi?: string[];
  /** Oznaczenie "ulubiony", przełączane gwiazdką na liście. Ten sam status
   * "opcjonalne, domyślnie fałsz" co pola wyżej. */
  ulubiony?: boolean;
}

export interface Setlista {
  id: string;
  nazwa: string;
  /** Identyfikatory utworów W KOLEJNOŚCI GRANIA. To jest cała treść setlisty -
   * przy ognisku brzeg ekranu przerzuca utwory po kolei, więc ta kolejność
   * staje się kolejnością grania. Wpis może wskazywać na utwór, którego już
   * nie ma w `utwory` (skasowany w międzyczasie) - to NIE jest błąd, patrz
   * `plik.ts` i `ui/setlista.ts`. */
  utwory: UtworId[];
  utworzonaO: string; // ISO 8601
}

const NAZWA_BAZY = 'ognisko';
// Etap 8 dokłada magazyn `setlisty`, stąd 1 -> 2. W bazie MH leżą już jego
// utwory, więc `onupgradeneeded` niżej jest napisane pod DWA możliwe punkty
// startu (0 -> 2 świeża instalacja, 1 -> 2 baza sprzed tego etapu) i celowo
// NIE rozgałęzia się po `event.oldVersion` - sprawdza obecność magazynu, nie
// numer wersji, więc żaden pośredni stan nie zostanie pominięty.
const WERSJA_SCHEMATU = 2;

/** Nazwa magazynu, eksportowana wyłącznie po to, żeby `plik.ts` mógł otworzyć
 * na niej WŁASNĄ transakcję (wyczyszczenie i wpisanie kompletu przy imporcie
 * musi być jedną transakcją, a żadna z funkcji poniżej takiej operacji
 * celowo nie robi, bo nie jest to zwykły zapis pojedynczego rekordu). */
export const MAGAZYN_UTWORY = 'utwory';
/** To samo uzasadnienie co przy `MAGAZYN_UTWORY` - import zastępuje OBA
 * magazyny w jednej transakcji (patrz `plik.ts`). */
export const MAGAZYN_SETLISTY = 'setlisty';

/** Błąd trwałości nazwany po polsku: ma dotrzeć do wywołującego jako
 * rozpoznawalny wyjątek, nigdy jako cisza (pusta lista, zawieszona obietnica). */
export class BladMagazynu extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BladMagazynu';
  }
}

// Baza otwiera się LENIWIE, przy pierwszym wywołaniu `otworzBaze()`, i tylko
// RAZ (wspólna obietnica): import tego modułu nie ma prawa wywrócić startu
// aplikacji, gdy IndexedDB jest niedostępne (np. tryb prywatny Safari).
let polaczenie: Promise<IDBDatabase> | null = null;

export function otworzBaze(): Promise<IDBDatabase> {
  if (polaczenie === null) {
    polaczenie = new Promise<IDBDatabase>((resolve, reject) => {
      if (!('indexedDB' in globalThis)) {
        reject(new BladMagazynu('IndexedDB niedostępne w tej przeglądarce.'));
        return;
      }

      const zadanie = indexedDB.open(NAZWA_BAZY, WERSJA_SCHEMATU);

      zadanie.onupgradeneeded = () => {
        const db = zadanie.result;
        // Zakłada KAŻDY magazyn tylko wtedy, gdy go jeszcze nie ma, i nigdy
        // nic w istniejącym magazynie nie czyści ani nie przepisuje - jedyna
        // droga do utraty śpiewnika MH prowadzi przez tę funkcję, więc jej
        // jedyny dozwolony efekt uboczny to `createObjectStore`.
        if (!db.objectStoreNames.contains(MAGAZYN_UTWORY)) {
          db.createObjectStore(MAGAZYN_UTWORY, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(MAGAZYN_SETLISTY)) {
          db.createObjectStore(MAGAZYN_SETLISTY, { keyPath: 'id' });
        }
      };

      // Inna karta czeka z podniesieniem schematu, a to połączenie (otwarte na
      // starszej wersji) blokuje jej postęp. Bez obsługi `blocked` aktualizacja
      // tamtej karty utknęłaby po cichu, więc melduje się to tutaj jako błąd.
      zadanie.onblocked = () => {
        reject(new BladMagazynu('Aktualizacja bazy zablokowana przez inną otwartą kartę ze śpiewnikiem.'));
      };

      zadanie.onsuccess = () => {
        const db = zadanie.result;
        // Odwrotny przypadek: to połączenie ma się grzecznie zamknąć, gdy TO
        // ONO stoi na drodze podniesienia schematu w innej karcie, inaczej
        // sam by wywołał scenariusz z `onblocked` wyżej, tyle że gdzie indziej.
        db.onversionchange = () => {
          db.close();
          // Zapamiętane połączenie trzeba PORZUCIĆ razem z zamknięciem, bo
          // wspólna obietnica oddawałaby dalej bazę już zamkniętą, a każda
          // kolejna transakcja na niej kończy się `InvalidStateError`.
          // Następne wywołanie ma po prostu otworzyć bazę od nowa, na nowym
          // schemacie, zamiast oddawać uchwyt, który nic już nie potrafi.
          polaczenie = null;
        };
        resolve(db);
      };

      zadanie.onerror = () => {
        reject(new BladMagazynu(`Nie udało się otworzyć bazy: ${zadanie.error?.message ?? 'nieznany błąd'}.`));
      };
    });
  }
  return polaczenie;
}

/** Komunikat błędu transakcji, wspólny dla zapisu i usunięcia: rozpoznaje
 * `QuotaExceededError` po nazwie i oddaje go jako czytelny powód po polsku,
 * zamiast zostawić wywołującego z gołym `DOMException`. */
function komunikatBleduTransakcji(blad: DOMException | null, czynnosc: string): string {
  if (blad?.name === 'QuotaExceededError') {
    return 'Przekroczono limit miejsca na dane w przeglądarce.';
  }
  return `${czynnosc} się nie powiodło: ${blad?.message ?? 'nieznany błąd'}.`;
}

/** Uruchamia `akcja` na `magazyn` i rozstrzyga wynik dopiero na
 * `transaction.oncomplete` - NIE na powodzeniu pojedynczego żądania (`put`
 * potrafi zgłosić sukces, a transakcja i tak wycofać się chwilę później).
 * Ogólna na nazwę magazynu (etap 8): `setlisty` ma dokładnie tę samą
 * dyscyplinę zapisu co `utwory`, więc druga, bliźniacza para funkcji
 * dublowałaby tylko tę logikę pod inną nazwą. */
function wTransakcji<T>(
  db: IDBDatabase,
  magazyn: string,
  tryb: IDBTransactionMode,
  czynnosc: string,
  akcja: (store: IDBObjectStore) => T,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const transakcja = db.transaction(magazyn, tryb);
    const store = transakcja.objectStore(magazyn);

    let wynik: T;
    try {
      wynik = akcja(store);
    } catch (e) {
      reject(e instanceof Error ? e : new BladMagazynu(String(e)));
      return;
    }

    transakcja.oncomplete = () => resolve(wynik);
    transakcja.onerror = () => reject(new BladMagazynu(komunikatBleduTransakcji(transakcja.error, czynnosc)));
    transakcja.onabort = () => reject(
      new BladMagazynu(`${czynnosc} przerwane, dane zostają w stanie sprzed operacji.`),
    );
  });
}

export async function zapiszUtwor(utwor: Utwor): Promise<void> {
  const db = await otworzBaze();
  await wTransakcji(db, MAGAZYN_UTWORY, 'readwrite', 'Zapis utworu', (store) => {
    store.put(utwor);
  });
}

/** Zapis wielu utworów w JEDNEJ transakcji: wchodzą wszystkie albo żaden. */
export async function zapiszUtworyRazem(utwory: readonly Utwor[]): Promise<void> {
  const db = await otworzBaze();
  await wTransakcji(db, MAGAZYN_UTWORY, 'readwrite', 'Zapis utworów', (store) => {
    for (const utwor of utwory) store.put(utwor);
  });
}

/** Odczyt jednej wartości z `IDBRequest`, rozstrzygany dopiero na
 * `transaction.oncomplete`: wartość z `onsuccess` jest już dostępna w
 * domknięciu w tym momencie, ale samo domknięcie ustawia się PRZED zwróceniem
 * `wynik` z `akcja()`, więc porządek przypisania jest jednoznaczny. Ogólna na
 * nazwę magazynu z tego samego powodu co `wTransakcji` wyżej. */
function wTransakcjiOdczyt<T>(
  db: IDBDatabase,
  magazyn: string,
  zadanie: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const transakcja = db.transaction(magazyn, 'readonly');
    const store = transakcja.objectStore(magazyn);
    const zadanieIdb = zadanie(store);
    let wynik: T;
    zadanieIdb.onsuccess = () => { wynik = zadanieIdb.result; };
    transakcja.oncomplete = () => resolve(wynik);
    transakcja.onerror = () => reject(new BladMagazynu(komunikatBleduTransakcji(transakcja.error, 'Odczyt')));
    transakcja.onabort = () => reject(new BladMagazynu('Odczyt przerwany.'));
  });
}

/** Uzupełnia `transpozycja`/`capo`/`tagi`/`ulubiony` wartościami domyślnymi,
 * gdy rekord ich nie ma (zapisany przed etapem, który je wprowadził).
 * Jedyne miejsce tego uzupełnienia - patrz komentarz przy tych polach w
 * `Utwor` wyżej - żeby reszta aplikacji mogła czytać `Utwor` bez pamiętania,
 * że któreś z tych pól bywa nieobecne. */
function uzupelnijPola(utwor: Utwor): Utwor {
  return {
    ...utwor,
    transpozycja: utwor.transpozycja ?? 0,
    capo: utwor.capo ?? 0,
    tagi: utwor.tagi ?? [],
    ulubiony: utwor.ulubiony ?? false,
  };
}

export async function pobierzUtwor(id: UtworId): Promise<Utwor | null> {
  const db = await otworzBaze();
  const wynik = await wTransakcjiOdczyt<Utwor | undefined>(db, MAGAZYN_UTWORY, (store) => store.get(id));
  return wynik ? uzupelnijPola(wynik) : null;
}

export async function listaUtworow(): Promise<Utwor[]> {
  const db = await otworzBaze();
  const wynik = await wTransakcjiOdczyt<Utwor[]>(db, MAGAZYN_UTWORY, (store) => store.getAll());
  return wynik.map(uzupelnijPola);
}

export async function usunUtwor(id: UtworId): Promise<void> {
  const db = await otworzBaze();
  await wTransakcji(db, MAGAZYN_UTWORY, 'readwrite', 'Usunięcie utworu', (store) => {
    store.delete(id);
  });
}

export async function policzUtwory(): Promise<number> {
  const db = await otworzBaze();
  return wTransakcjiOdczyt<number>(db, MAGAZYN_UTWORY, (store) => store.count());
}

// -------------------------------------------------------------- setlisty (etap 8)

export async function listaSetlist(): Promise<Setlista[]> {
  const db = await otworzBaze();
  return wTransakcjiOdczyt<Setlista[]>(db, MAGAZYN_SETLISTY, (store) => store.getAll());
}

export async function zapiszSetliste(setlista: Setlista): Promise<void> {
  const db = await otworzBaze();
  await wTransakcji(db, MAGAZYN_SETLISTY, 'readwrite', 'Zapis setlisty', (store) => {
    store.put(setlista);
  });
}

export async function usunSetliste(id: string): Promise<void> {
  const db = await otworzBaze();
  await wTransakcji(db, MAGAZYN_SETLISTY, 'readwrite', 'Usunięcie setlisty', (store) => {
    store.delete(id);
  });
}
